import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axiosInstance, { getUserDetails, hasScientistPondUIAccess } from '../../components/content/axiosConfig';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import FishSpeciesDropDownSelection from '../../components/custom/FishSpeciesDropDownSelection';
import WeightUnitDropDownSelection from '../../components/custom/WeightUnitDropDown';
import LengthUnitDropDownSelection from '../../components/custom/LengthUnitDropDownSelection';
import { queryClient } from '../../hooks/queries/queryClient';
import { queryKeys } from '../../hooks/queries/queryKeys';
import { normalizeFishTypesData } from '../../hooks/queries/useFishTypes';
import { normalizeUnitsData } from '../../hooks/queries/useLengthUnits';
import { useInvalidateCache } from '../../hooks/queries/useInvalidateCache';
import { toDateInputValue } from '../../utils/toDateInputValue';
import { SpeciesTableLabel } from '../../utils/samplingDisplayLabels';
import {
  computePredatorWsWr,
  buildPredatorOverallSummaries,
  predatorRowHeadcount,
  predatorRowLabelForExport,
} from '../../utils/stockingPredatorMath';

const STOCKING_EVENTS_PAGE_SIZE = 10;

function parseStockingEventsApiResponse(body) {
  if (Array.isArray(body)) {
    return {
      events: body,
      total: body.length,
      page: 1,
    };
  }
  return {
    events: body?.data ?? [],
    total: Number(body?.total) || 0,
    page: Number(body?.page) || 1,
  };
}

function formatStockingUnitsDisplay(ev) {
  const w = ev.weightUnit?.Name;
  const l = ev.lengthUnit?.Name;
  if (w && l) return `${w} / ${l}`;
  if (w) return w;
  if (l) return l;
  return '—';
}

function formatWrAsPercent(wr) {
  if (wr === '' || wr === undefined || wr === null) return '';
  const num = parseFloat(wr);
  if (Number.isNaN(num)) return '';
  return `${(num * 100).toFixed(1)}%`;
}

function getPredatorDataWorksheet(workbook) {
  if (!workbook?.SheetNames?.length) return null;
  const names = workbook.SheetNames;
  const idx = names.findIndex((n) => String(n).trim().toLowerCase() === 'predator data');
  const sheetName = idx >= 0 ? names[idx] : names[0];
  return workbook.Sheets[sheetName] || null;
}

/** Read first matching key from Excel row (handles spacing / case variants). */
function excelCell(row, candidates) {
  if (!row || typeof row !== 'object') return '';
  const keys = Object.keys(row);
  for (const c of candidates) {
    if (row[c] != null && String(row[c]).trim() !== '') return row[c];
    const cNorm = String(c).replace(/\s+/g, '').toLowerCase();
    const hit = keys.find((k) => String(k).replace(/\s+/g, '').toLowerCase() === cNorm);
    if (hit != null && row[hit] != null && String(row[hit]).trim() !== '') return row[hit];
  }
  return '';
}

function parsePredatorExcelRow(row) {
  const fishTypeName = String(
    excelCell(row, [
      'FishTypeName',
      'Type',
      'Species',
      'FishType',
      'PredatorTypeName',
      'Fish Type',
    ])
  ).trim();
  const length = excelCell(row, ['Length', 'AVGLength', 'Avg length', 'AVG len']);
  const weight = excelCell(row, ['Weight', 'TotalWeight', 'Total weight']);
  let tag = excelCell(row, ['Tag', 'TagNumber', 'Tag #', 'Tag#']);
  // Older templates used a "Number" column; map into Tag # when Tag is empty so imports still work.
  if ((tag === '' || tag == null) && row) {
    const legacy = excelCell(row, ['Number', 'Count', 'TotalNumber', 'Total number']);
    if (legacy != null && String(legacy).trim() !== '') {
      tag = legacy;
    }
  }
  return { fishTypeName, length, weight, tag };
}

function rowLooksEmptyPredator(parsed) {
  return (
    !parsed.fishTypeName &&
    (parsed.length === '' || parsed.length == null) &&
    (parsed.weight === '' || parsed.weight == null) &&
    (parsed.tag === '' || parsed.tag == null)
  );
}

function resolvePredatorFishTypeFromName(name, pondFishSpeciesOptions) {
  const t = String(name).trim();
  if (!t) return null;
  const lo = t.toLowerCase();
  const opt = (pondFishSpeciesOptions || []).find(
    (o) => String(o.label).trim().toLowerCase() === lo
  );
  return opt ? opt.value : null;
}

/** Sum of predator Number column (same rules as save payload); if no numeric field on any row, use row count. */
const sumPredatorHeadcount = (rows) => {
  const r = rows || [];
  let sum = 0;
  let anyNumeric = false;
  for (const row of r) {
    const n = predatorRowHeadcount(row);
    if (n != null) {
      anyNumeric = true;
      sum += n;
    }
  }
  const stockedRows = r.filter(
    (row) => row && row.FishTypeID != null && row.FishTypeID !== ''
  );
  if (anyNumeric && sum > 0) return sum;
  if (!anyNumeric) return stockedRows.length;
  if (sum === 0 && stockedRows.length > 0) return stockedRows.length;
  return sum;
};

function formatPredatorSummaryNumber(n) {
  if (n == null || Number.isNaN(n)) return '—';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

/** Single-row result for predator Excel import (validation + built row). */
function evaluatePredatorExcelRowForImport(raw, pondFishSpeciesOptions) {
  const parsed = parsePredatorExcelRow(raw);
  if (rowLooksEmptyPredator(parsed)) {
    return { status: 'skipped', parsed, message: 'Empty row' };
  }
  if (!parsed.fishTypeName) {
    return {
      status: 'error',
      parsed,
      message: 'FishTypeName is required when other columns are filled.',
    };
  }
  const fid = resolvePredatorFishTypeFromName(parsed.fishTypeName, pondFishSpeciesOptions);
  if (fid == null || fid === '') {
    return {
      status: 'error',
      parsed,
      message: `Fish type "${parsed.fishTypeName}" does not match this pond's fish species list.`,
    };
  }
  return {
    status: 'ok',
    parsed,
    row: {
      FishTypeID: fid,
      Number: '',
      Length:
        parsed.length != null && parsed.length !== ''
          ? String(parsed.length).trim()
          : '',
      Weight:
        parsed.weight != null && parsed.weight !== ''
          ? String(parsed.weight).trim()
          : '',
      Tag: parsed.tag != null && parsed.tag !== '' ? String(parsed.tag).trim() : '',
    },
  };
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('read failed'));
    reader.readAsArrayBuffer(file);
  });
}

const emptyPreyRow = () => ({
  FishTypeID: null,
  Tag: '',
  TotalWeight: '',
  MinLength: '',
});

/** Same shape as forage: total # (Tag), total wt, avg len, avg wt computed. */
const emptyPredatorBulkRow = emptyPreyRow;

/** Whole-number count from forage row "Total number" field (Tag in UI, Number in saved API rows). */
const parsePreyTotalNumber = (row) => {
  const fromTag = row.Tag != null ? String(row.Tag).trim() : '';
  const fromNumber = row.Number != null ? String(row.Number).trim() : '';
  const rawValue = fromTag || fromNumber;
  const normalized = rawValue.replaceAll(',', '');
  const parsed = normalized === '' ? NaN : parseInt(normalized, 10);
  const isWholeNumber = !Number.isNaN(parsed) && String(parsed) === normalized;
  return isWholeNumber ? Math.max(0, parsed) : null;
};

/** Sum of forage "# TOTAL #" values (Tag when it encodes a whole-number count). */
const sumPreyTotalNumbers = (rows) => {
  let sum = 0;
  for (const row of rows || []) {
    const n = parsePreyTotalNumber(row);
    if (n != null) sum += n;
  }
  return sum;
};

const forageTypeLabelFromApiRow = (r) => {
  const fromFish = r.fishType?.Name ?? r.fishType?.name;
  if (fromFish != null && String(fromFish).trim()) return String(fromFish).trim();
  if (r.ForageTypeName != null && String(r.ForageTypeName).trim()) {
    return String(r.ForageTypeName).trim();
  }
  if (r.FishTypeID != null && r.FishTypeID !== '') return String(r.FishTypeID);
  return '—';
};

const forageRowIsListed = (r) => {
  if (!r) return false;
  if (r.FishTypeID != null && r.FishTypeID !== '') return true;
  if (r.ForageTypeName != null && String(r.ForageTypeName).trim()) return true;
  return false;
};

const formatForageTotalNumberDisplay = (r) => {
  const n = parsePreyTotalNumber(r);
  if (n != null) return n.toLocaleString();
  const tag = r.Tag != null ? String(r.Tag).trim() : '';
  if (tag) return tag;
  return '—';
};

const formatStockingFloatCell = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const num = typeof v === 'number' && Number.isFinite(v) ? v : parseFloat(v);
  if (Number.isNaN(num)) return '—';
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const formatForageAvgWeightFromApiRow = (r) => {
  if (r.MaxLength != null && r.MaxLength !== '') {
    const num = parseFloat(r.MaxLength);
    if (!Number.isNaN(num)) {
      return Number.isInteger(num) ? String(num) : num.toFixed(2);
    }
  }
  return formatPreyAvgWeightDisplay({
    Tag: r.Tag != null ? String(r.Tag) : '',
    Number: r.Number != null ? String(r.Number) : '',
    TotalWeight: r.TotalWeight != null ? String(r.TotalWeight) : '',
  });
};

const getLengthUnitNameFromEvent = (ev) => {
  const u = ev.lengthUnit;
  if (!u) return '';
  const name = u.Name ?? u.name;
  return name != null ? String(name) : '';
};

const buildEventPredatorSummaries = (ev, pondFishSpeciesOptions, fishTypesById) => {
  const rows = (ev.predatorRows || []).map((row) => ({
    FishTypeID: row.FishTypeID,
    Number: row.Number != null ? String(row.Number) : '',
    Length: row.Length != null ? String(row.Length) : '',
    Weight: row.Weight != null ? String(row.Weight) : '',
    TotalWeight: row.TotalWeight != null && row.TotalWeight !== '' ? String(row.TotalWeight) : '',
    Tag: row.Tag != null ? String(row.Tag) : '',
    IsBatch: !!(row.IsBatch === true || row.IsBatch === 1),
  }));
  return buildPredatorOverallSummaries(
    rows,
    pondFishSpeciesOptions,
    fishTypesById,
    getLengthUnitNameFromEvent(ev)
  );
};

/** AVG weight = total weight / total number (stored in API as MaxLength for compatibility). */
const computePreyAvgWeight = (row) => {
  const n = parsePreyTotalNumber(row);
  const w =
    row.TotalWeight === '' || row.TotalWeight == null ? NaN : parseFloat(row.TotalWeight);
  if (n == null || n <= 0 || Number.isNaN(w)) return null;
  const avg = w / n;
  return Number.isFinite(avg) ? avg : null;
};

const formatPreyAvgWeightDisplay = (row) => {
  const aw = computePreyAvgWeight(row);
  if (aw == null) return '';
  return Number.isInteger(aw) ? String(aw) : aw.toFixed(2);
};

const emptyPredatorRow = () => ({
  FishTypeID: null,
  Number: '',
  Length: '',
  Weight: '',
  Tag: '',
});

const normalizeForageValues = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object' && item.value) return String(item.value).trim();
        return '';
      })
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .filter((part) => part !== '[object Object]');
  }

  if (typeof value === 'object' && value.value) {
    return [String(value.value).trim()].filter(Boolean);
  }

  return [];
};

/** Preserve event forage fields on edit (managed on pond General tab for new data). */
const forageTypesToApiString = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') return raw.trim() || null;
  const joined = normalizeForageValues(raw).join(',');
  return joined || null;
};

const additionalForageTokens = (str) =>
  str && String(str).trim()
    ? String(str)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

/**
 * Forage type dropdown = pond Forage + Additional forage only (no pond “fish species” list).
 * Values resolve to Fish Type id when the label matches a Fish Types record.
 */
const buildPreySpeciesOptions = (allFishTypes, pondForage, pondForageType) => {
  const byKey = new Map();
  const add = (value, label) => {
    const key =
      typeof value === 'number' && Number.isFinite(value)
        ? `id:${value}`
        : `name:${String(value).trim().toLowerCase()}`;
    if (!byKey.has(key)) byKey.set(key, { value, label });
  };

  const tokens = [
    ...normalizeForageValues(pondForage),
    ...additionalForageTokens(pondForageType),
  ];
  const seen = new Set();
  for (const token of tokens) {
    const lo = token.trim().toLowerCase();
    if (!lo || seen.has(lo)) continue;
    seen.add(lo);
    const ft = (allFishTypes || []).find(
      (x) => String(x.Name).trim().toLowerCase() === lo
    );
    if (ft) add(ft.id, ft.Name);
    else add(token.trim(), token.trim());
  }

  return Array.from(byKey.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
  );
};

const preyOptionKey = (o) =>
  typeof o.value === 'number' && Number.isFinite(o.value)
    ? `id:${o.value}`
    : `name:${String(o.value).trim().toLowerCase()}`;

/** So edited events that used a fish type not in forage list still show a label in the select. */
const mergePreyOptionsFromEventRows = (base, preyApiRows) => {
  const byKey = new Map((base || []).map((o) => [preyOptionKey(o), o]));
  for (const r of preyApiRows || []) {
    const forageName =
      r.ForageTypeName != null && String(r.ForageTypeName).trim()
        ? String(r.ForageTypeName).trim()
        : null;
    const fromFishType = r.fishType?.Name ?? r.fishType?.name;
    const value =
      r.FishTypeID != null && r.FishTypeID !== ''
        ? r.FishTypeID
        : forageName;
    const label = fromFishType || forageName || (value != null ? String(value) : '');
    if (value == null || value === '' || !String(label).trim()) continue;
    const o = { value, label };
    const k = preyOptionKey(o);
    if (!byKey.has(k)) byKey.set(k, o);
  }
  return Array.from(byKey.values()).sort((a, b) =>
    String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' })
  );
};

const StockingData = () => {
  const { id: pondID } = useParams();
  const userDetails = getUserDetails();
  const [pondFisherID, setPondFisherID] = useState(null);
  const fisherID = userDetails.FisherID ?? pondFisherID;
  const canEditStocking = hasScientistPondUIAccess(userDetails);
  const { invalidatePond, invalidateDashboardOwner } = useInvalidateCache();
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [events, setEvents] = useState([]);
  const [preySpeciesOptions, setPreySpeciesOptions] = useState([]);
  const [pondFishSpeciesOptions, setPondFishSpeciesOptions] = useState([]);
  const [allFishTypes, setAllFishTypes] = useState([]);
  const [lengthUnitsList, setLengthUnitsList] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [formStockingDate, setFormStockingDate] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formIsInitial, setFormIsInitial] = useState(false);
  const [selectedWeightUnit, setSelectedWeightUnit] = useState('');
  const [selectedLengthUnit, setSelectedLengthUnit] = useState('');
  const [formForagePounds, setFormForagePounds] = useState('');
  /** Carried through save on edit only; new events send null (pond General tab holds forage types). */
  const [preservedForageTypes, setPreservedForageTypes] = useState(null);
  const [preservedAdditionalForageType, setPreservedAdditionalForageType] = useState(null);
  const [preyRows, setPreyRows] = useState([emptyPreyRow()]);
  const [predatorBulkRows, setPredatorBulkRows] = useState([emptyPredatorBulkRow()]);
  const [predatorRows, setPredatorRows] = useState([emptyPredatorRow()]);
  const [showEventForm, setShowEventForm] = useState(false);
  const basePreySpeciesOptionsRef = useRef([]);
  const stockingPredatorExcelInputRef = useRef(null);

  const [predatorImportModalOpen, setPredatorImportModalOpen] = useState(false);
  const [predatorImportFile, setPredatorImportFile] = useState(null);
  const [predatorImportPreview, setPredatorImportPreview] = useState([]);
  const [isPredatorImporting, setIsPredatorImporting] = useState(false);
  const [predatorImportProgress, setPredatorImportProgress] = useState({
    current: 0,
    total: 0,
    currentRow: null,
  });
  const [predatorImportProcessedRows, setPredatorImportProcessedRows] = useState([]);
  const [predatorImportReportComplete, setPredatorImportReportComplete] = useState(false);
  /** Event ids (string) with forage/predator detail rows collapsed in the list */
  const [collapsedStockingDetailIds, setCollapsedStockingDetailIds] = useState(() => new Set());
  const [stockingEventsPage, setStockingEventsPage] = useState(1);
  const [stockingEventsTotal, setStockingEventsTotal] = useState(0);
  const [stockingEventsListLoading, setStockingEventsListLoading] = useState(false);

  const toggleStockingEventDetails = useCallback((eventId) => {
    const key = String(eventId);
    setCollapsedStockingDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const fishTypesById = useMemo(() => {
    const m = new Map();
    for (const ft of allFishTypes) {
      const rawId = ft.id ?? ft.ID;
      const id = rawId != null ? Number(rawId) : NaN;
      if (Number.isFinite(id)) {
        m.set(id, { WsIP: ft.WsIP, WsMG: ft.WsMG, Name: ft.Name });
      }
    }
    return m;
  }, [allFishTypes]);

  const selectedLengthUnitName = useMemo(() => {
    if (selectedLengthUnit === '' || selectedLengthUnit == null) return '';
    const id = Number(selectedLengthUnit);
    const u = lengthUnitsList.find((x) => Number(x.id ?? x.ID) === id);
    const name = u?.Name ?? u?.name;
    return name != null ? String(name) : '';
  }, [selectedLengthUnit, lengthUnitsList]);

  const predatorRowsForFormSummary = useMemo(() => {
    const bulk = predatorBulkRows.map((r) => ({
      FishTypeID: r.FishTypeID,
      Number: '',
      Length: r.MinLength,
      Weight: '',
      TotalWeight: r.TotalWeight,
      Tag: r.Tag,
      IsBatch: true,
    }));
    const indiv = predatorRows.map((r) => ({ ...r, IsBatch: false }));
    return [...bulk, ...indiv];
  }, [predatorBulkRows, predatorRows]);

  const predatorOverallSummaries = useMemo(
    () =>
      buildPredatorOverallSummaries(
        predatorRowsForFormSummary,
        pondFishSpeciesOptions,
        fishTypesById,
        selectedLengthUnitName
      ),
    [predatorRowsForFormSummary, pondFishSpeciesOptions, fishTypesById, selectedLengthUnitName]
  );

  const getPredatorWsWrForRow = useCallback(
    (row) => {
      const ft = fishTypesById.get(Number(row.FishTypeID));
      return computePredatorWsWr({
        lengthStr: row.Length,
        weightStr: row.Weight,
        wsMG: ft?.WsMG,
        wsIP: ft?.WsIP,
        lengthUnitName: selectedLengthUnitName,
      });
    },
    [fishTypesById, selectedLengthUnitName]
  );

  const closePredatorImportModal = useCallback(() => {
    setPredatorImportModalOpen(false);
    setPredatorImportFile(null);
    setPredatorImportPreview([]);
    setIsPredatorImporting(false);
    setPredatorImportProgress({ current: 0, total: 0, currentRow: null });
    setPredatorImportProcessedRows([]);
    setPredatorImportReportComplete(false);
    if (stockingPredatorExcelInputRef.current) {
      stockingPredatorExcelInputRef.current.value = '';
    }
  }, []);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setFormStockingDate('');
    setFormNotes('');
    setFormIsInitial(false);
    setSelectedWeightUnit('');
    setSelectedLengthUnit('');
    setFormForagePounds('');
    setPreservedForageTypes(null);
    setPreservedAdditionalForageType(null);
    setPreyRows([emptyPreyRow()]);
    setPredatorBulkRows([emptyPredatorBulkRow()]);
    setPredatorRows([emptyPredatorRow()]);
    closePredatorImportModal();
  }, [closePredatorImportModal]);

  const closeEventForm = useCallback(() => {
    resetForm();
    setShowEventForm(false);
    setPreySpeciesOptions([...basePreySpeciesOptionsRef.current]);
  }, [resetForm]);

  const openAddEventForm = useCallback(() => {
    resetForm();
    setPreySpeciesOptions([...basePreySpeciesOptionsRef.current]);
    setShowEventForm(true);
    setTimeout(() => {
      document.getElementById('stocking-event-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }, [resetForm]);

  const stockingFisherParams = useCallback(() => {
    if (fisherID == null || fisherID === '') return {};
    return { FisherID: fisherID };
  }, [fisherID]);

  const fetchStockingEventsList = useCallback(
    async (page = 1, showTableLoading = true) => {
      if (showTableLoading) setStockingEventsListLoading(true);
      try {
        let requestPage = Math.max(1, page);
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const res = await axiosInstance.get(`/api/PondStocking/GetByPond/${pondID}`, {
            params: {
              ...stockingFisherParams(),
              page: requestPage,
              pageSize: STOCKING_EVENTS_PAGE_SIZE,
            },
          });
          const parsed = parseStockingEventsApiResponse(res.data);
          if (parsed.events.length === 0 && requestPage > 1 && parsed.total > 0) {
            requestPage -= 1;
            continue;
          }
          setEvents(parsed.events);
          setStockingEventsTotal(parsed.total);
          setStockingEventsPage(requestPage);
          break;
        }
      } catch (error) {
        showErrorToast(error.response?.data?.message || 'Failed to load stocking data');
        setEvents([]);
        setStockingEventsTotal(0);
        setStockingEventsPage(1);
      } finally {
        if (showTableLoading) setStockingEventsListLoading(false);
      }
    },
    [pondID, stockingFisherParams]
  );

  const stockingEventsTotalPages = Math.max(
    1,
    Math.ceil(stockingEventsTotal / STOCKING_EVENTS_PAGE_SIZE)
  );
  const stockingEventsRangeStart =
    stockingEventsTotal === 0 ? 0 : (stockingEventsPage - 1) * STOCKING_EVENTS_PAGE_SIZE + 1;
  const stockingEventsRangeEnd = Math.min(
    stockingEventsPage * STOCKING_EVENTS_PAGE_SIZE,
    stockingEventsTotal
  );

  const handleStockingEventsPageChange = useCallback(
    (nextPage) => {
      const p = Math.max(1, Math.min(nextPage, stockingEventsTotalPages));
      if (p !== stockingEventsPage) fetchStockingEventsList(p, true);
    },
    [fetchStockingEventsList, stockingEventsPage, stockingEventsTotalPages]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPageLoading(true);
      try {
        const pondRes = await axiosInstance.get(`/api/Pond/GetPondByID`, { params: { id: pondID } });
        const pond = pondRes.data || {};
        const effectiveFisherID = userDetails.FisherID ?? pond.FisherID;
        if (!cancelled) {
          setPondFisherID(pond.FisherID ?? null);
        }

        const fisherParams =
          effectiveFisherID != null && effectiveFisherID !== ''
            ? { FisherID: effectiveFisherID }
            : {};

        const [stockingRes, pondFishesRes, fishTypesRes, lengthUnitsRes] = await Promise.all([
            axiosInstance.get(`/api/PondStocking/GetByPond/${pondID}`, {
              params: {
                ...fisherParams,
                page: 1,
                pageSize: STOCKING_EVENTS_PAGE_SIZE,
              },
            }),
            axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondID}`),
            queryClient.fetchQuery({
              queryKey: queryKeys.fishTypes({ search: '' }),
              queryFn: () =>
                axiosInstance.get(`/api/FishTypes/GetAllFishTypes`).then((r) => r.data),
            }),
            queryClient.fetchQuery({
              queryKey: queryKeys.lengthUnits({}),
              queryFn: () =>
                axiosInstance.get(`/api/LengthUnit/GetAll`).then((r) => r.data),
            }),
          ]);

        if (!cancelled) {
          const parsed = parseStockingEventsApiResponse(stockingRes.data);
          setEvents(parsed.events);
          setStockingEventsTotal(parsed.total);
          setStockingEventsPage(parsed.page);
          const pondFish = pondFishesRes.data || [];
          const pondOpts = pondFish.map((fish) => ({
            value: fish.FishTypeID,
            label: fish.FishTypeName,
            abbreviation:
              fish.FishTypeAbbreviation || fish.Abbreviation || '',
          }));
          setPondFishSpeciesOptions(pondOpts);
          const fishTypesData = normalizeFishTypesData(fishTypesRes);
          setAllFishTypes(fishTypesData);
          setLengthUnitsList(normalizeUnitsData(lengthUnitsRes));
          const preyOpts = buildPreySpeciesOptions(
            fishTypesData,
            pond.Forage,
            pond.ForageType
          );
          basePreySpeciesOptionsRef.current = preyOpts;
          setPreySpeciesOptions(preyOpts);
        }
      } catch (error) {
        if (!cancelled) {
          showErrorToast(error.response?.data?.message || 'Failed to load stocking data');
          setEvents([]);
          setStockingEventsTotal(0);
          setStockingEventsPage(1);
          setPondFishSpeciesOptions([]);
          setAllFishTypes([]);
          setLengthUnitsList([]);
          basePreySpeciesOptionsRef.current = [];
          setPreySpeciesOptions([]);
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pondID, userDetails.FisherID]);

  const generatePredatorExcelTemplate = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([['FishTypeName', 'Length', 'Weight', 'Tag']]);
    ws['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 22 }];

    const listRows = [
      ['Predator fish type names (use exactly as listed — species stocked in this pond):'],
      ['Instructions:'],
      ['1. Fill the "Predator Data" sheet: FishTypeName must match a name below (copy/paste recommended).'],
      [
        '2. Columns match the form: Length, Weight, and Tag (#). Use Tag for tag ID or a whole-number count — same as the stocking form.',
      ],
      [
        '3. Ws and Wr appear on data export from the form only; import still uses the four columns on the Predator Data sheet.',
      ],
      [],
      ['Fish species for this pond:'],
      ...(pondFishSpeciesOptions.length
        ? pondFishSpeciesOptions.map((o) => [o.label])
        : [
            [
              '(No species yet — add fish to this pond first, then download this template again.)',
            ],
          ]),
    ];
    const wsList = XLSX.utils.aoa_to_sheet(listRows);
    wsList['!cols'] = [{ wch: 72 }];

    XLSX.utils.book_append_sheet(wb, ws, 'Predator Data');
    XLSX.utils.book_append_sheet(wb, wsList, 'Pond Fish Species List');
    XLSX.writeFile(wb, 'stocking_predator_template.xlsx');
  }, [pondFishSpeciesOptions]);

  const exportPredatorRowsToExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const header = [['FishTypeName', 'Length', 'Weight', 'Ws', 'Wr (%)', 'Tag']];
    const dataRows = predatorRows.map((r) => {
      const tagStr = r.Tag != null ? String(r.Tag).trim() : '';
      const tagCol =
        tagStr !== ''
          ? tagStr
          : r.Number != null && String(r.Number).trim() !== ''
            ? String(r.Number).trim()
            : '';
      const ft = fishTypesById.get(Number(r.FishTypeID));
      const { ws, wr } = computePredatorWsWr({
        lengthStr: r.Length,
        weightStr: r.Weight,
        wsMG: ft?.WsMG,
        wsIP: ft?.WsIP,
        lengthUnitName: selectedLengthUnitName,
      });
      return [
        predatorRowLabelForExport(r, pondFishSpeciesOptions),
        r.Length != null && r.Length !== '' ? String(r.Length) : '',
        r.Weight != null && r.Weight !== '' ? String(r.Weight) : '',
        ws === '' ? '' : ws,
        formatWrAsPercent(wr),
        tagCol,
      ];
    });
    const wsSheet = XLSX.utils.aoa_to_sheet([...header, ...dataRows]);
    wsSheet['!cols'] = [
      { wch: 30 },
      { wch: 12 },
      { wch: 12 },
      { wch: 10 },
      { wch: 10 },
      { wch: 22 },
    ];
    XLSX.utils.book_append_sheet(wb, wsSheet, 'Predator Data');
    XLSX.writeFile(wb, 'stocking_predator_data.xlsx');
  }, [predatorRows, pondFishSpeciesOptions, fishTypesById, selectedLengthUnitName]);

  const applyPredatorImportFile = useCallback((file) => {
    setPredatorImportReportComplete(false);
    setPredatorImportProcessedRows([]);
    if (!file) {
      setPredatorImportFile(null);
      setPredatorImportPreview([]);
      return;
    }
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      showErrorToast('Please choose an Excel file (.xlsx or .xls)');
      return;
    }
    setPredatorImportFile(file);
    readFileAsArrayBuffer(file)
      .then((buf) => {
        try {
          const wb = XLSX.read(new Uint8Array(buf), { type: 'array' });
          const ws = getPredatorDataWorksheet(wb);
          if (!ws) {
            setPredatorImportPreview([]);
            return;
          }
          const rows = XLSX.utils.sheet_to_json(ws).slice(0, 15);
          setPredatorImportPreview(rows);
        } catch {
          setPredatorImportPreview([]);
        }
      })
      .catch(() => setPredatorImportPreview([]));
  }, []);

  const handlePredatorImportFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (event.target) event.target.value = '';
      applyPredatorImportFile(file);
    },
    [applyPredatorImportFile]
  );

  const handlePredatorImportDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      applyPredatorImportFile(e.dataTransfer.files?.[0]);
    },
    [applyPredatorImportFile]
  );

  const handlePredatorImportDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePredatorImportConfirm = useCallback(async () => {
    if (!predatorImportFile) {
      showErrorToast('Select an Excel file first.');
      return;
    }
    setIsPredatorImporting(true);
    setPredatorImportProcessedRows([]);
    setPredatorImportReportComplete(false);
    try {
      const buf = await readFileAsArrayBuffer(predatorImportFile);
      const workbook = XLSX.read(new Uint8Array(buf), { type: 'array' });
      const worksheet = getPredatorDataWorksheet(workbook);
      if (!worksheet) {
        showErrorToast('Could not read a sheet from the workbook.');
        setIsPredatorImporting(false);
        setPredatorImportReportComplete(true);
        return;
      }
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const processed = [];
      const okRows = [];
      for (let i = 0; i < jsonData.length; i++) {
        const raw = jsonData[i];
        const excelRow = i + 2;
        const label =
          String(
            excelCell(raw, [
              'FishTypeName',
              'Type',
              'Species',
              'FishType',
              'PredatorTypeName',
            ]) || ''
          ).trim() || '—';
        setPredatorImportProgress({
          current: i + 1,
          total: jsonData.length,
          currentRow: { excelRow, label },
        });
        const ev = evaluatePredatorExcelRowForImport(raw, pondFishSpeciesOptions);
        if (ev.status === 'skipped') {
          processed.push({
            excelRow,
            status: 'skipped',
            label,
            detail: ev.message,
          });
        } else if (ev.status === 'error') {
          processed.push({
            excelRow,
            status: 'error',
            label,
            detail: ev.message,
          });
        } else {
          okRows.push(ev.row);
          processed.push({
            excelRow,
            status: 'success',
            label: ev.parsed.fishTypeName,
            detail: 'Imported',
          });
        }
        setPredatorImportProcessedRows([...processed]);
        await new Promise((r) => setTimeout(r, 32));
      }
      const errCount = processed.filter((p) => p.status === 'error').length;
      const skipCount = processed.filter((p) => p.status === 'skipped').length;
      setPredatorImportReportComplete(true);
      setIsPredatorImporting(false);
      setPredatorImportProgress((prev) => ({ ...prev, currentRow: null }));
      if (errCount > 0) {
        showErrorToast(
          `Import finished with ${errCount} error(s). Predator rows were not updated. See the report below.`
        );
        return;
      }
      if (okRows.length === 0) {
        showErrorToast('No predator rows to import (only empty rows).');
        return;
      }
      setPredatorRows(okRows);
      showSuccessToast(
        `Imported ${okRows.length} predator row(s). ${skipCount} empty row(s) skipped.`
      );
      closePredatorImportModal();
    } catch (e) {
      console.error(e);
      showErrorToast('Failed to read Excel file.');
      setIsPredatorImporting(false);
      setPredatorImportReportComplete(true);
    }
  }, [predatorImportFile, pondFishSpeciesOptions, closePredatorImportModal]);

  const buildPreyPayload = () =>
    preyRows
      .filter((r) => r.FishTypeID != null && r.FishTypeID !== '')
      .map((r) => {
        const rawTag = r.Tag != null ? String(r.Tag).trim() : '';
        const parsed = rawTag === '' ? NaN : parseInt(rawTag, 10);
        const isWholeNumber = !Number.isNaN(parsed) && String(parsed) === rawTag;
        const sel = r.FishTypeID;
        let fishTypeIDPayload;
        if (typeof sel === 'number' && Number.isFinite(sel)) {
          fishTypeIDPayload = Math.trunc(sel);
        } else {
          const s = String(sel).trim();
          fishTypeIDPayload = /^\d+$/.test(s) ? parseInt(s, 10) : s;
        }
        const avgWeight = computePreyAvgWeight(r);
        return {
          FishTypeID: fishTypeIDPayload,
          Number: isWholeNumber ? Math.max(0, parsed) : 0,
          Tag: rawTag && !isWholeNumber ? rawTag : null,
          TotalWeight: r.TotalWeight === '' ? null : parseFloat(r.TotalWeight),
          MinLength: r.MinLength === '' ? null : parseFloat(r.MinLength),
          MaxLength: avgWeight == null ? null : avgWeight,
        };
      });

  const buildPredBulkPayload = () =>
    predatorBulkRows
      .filter((r) => r.FishTypeID != null && r.FishTypeID !== '')
      .map((r) => {
        const n = parsePreyTotalNumber({ Tag: r.Tag, Number: r.Number });
        return {
          FishTypeID: parseInt(r.FishTypeID, 10),
          Number: n != null ? n : 0,
          Length: r.MinLength === '' ? null : parseFloat(r.MinLength),
          Weight: null,
          TotalWeight: r.TotalWeight === '' ? null : parseFloat(r.TotalWeight),
          Ws: null,
          Wr: null,
          Tag: null,
          IsBatch: true,
        };
      });

  const buildPredPayload = () =>
    predatorRows
      .filter((r) => r.FishTypeID != null && r.FishTypeID !== '')
      .map((r) => {
        const rawTag = r.Tag != null ? String(r.Tag).trim() : '';
        const rawNumber = r.Number != null ? String(r.Number).trim() : '';
        const parsedNumberFromNumber = rawNumber === '' ? NaN : parseInt(rawNumber, 10);
        const ft = fishTypesById.get(Number(r.FishTypeID));
        const { ws, wr } = computePredatorWsWr({
          lengthStr: r.Length,
          weightStr: r.Weight,
          wsMG: ft?.WsMG,
          wsIP: ft?.WsIP,
          lengthUnitName: selectedLengthUnitName,
        });

        return {
          FishTypeID: parseInt(r.FishTypeID, 10),
          Number: !Number.isNaN(parsedNumberFromNumber)
            ? parsedNumberFromNumber
            : 0,
          Length: r.Length === '' ? null : parseFloat(r.Length),
          Weight: r.Weight === '' ? null : parseFloat(r.Weight),
          TotalWeight: null,
          Ws: ws === '' ? null : ws,
          Wr: wr === '' ? null : wr,
          Tag: rawTag || null,
          IsBatch: false,
        };
      });

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    const prey = buildPreyPayload();
    const pred = [...buildPredBulkPayload(), ...buildPredPayload()];
    if (prey.length === 0 && pred.length === 0) {
      showErrorToast('Add at least one forage or predator row with a fish type');
      return;
    }
    try {
      setSaving(true);
      const body = {
        FisherID: fisherID,
        StockingDate: formStockingDate || null,
        Notes: formNotes || null,
        IsInitial: formIsInitial,
        Weight_Unit:
          selectedWeightUnit === '' || selectedWeightUnit == null
            ? null
            : Number(selectedWeightUnit),
        Length_Unit:
          selectedLengthUnit === '' || selectedLengthUnit == null
            ? null
            : Number(selectedLengthUnit),
        ForageTypes: preservedForageTypes,
        AdditionalForageType:
          preservedAdditionalForageType && String(preservedAdditionalForageType).trim()
            ? String(preservedAdditionalForageType).trim()
            : null,
        ForagePoundsAdded:
          formForagePounds === '' || formForagePounds == null
            ? null
            : parseFloat(formForagePounds),
        preyRows: prey,
        predatorRows: pred,
      };
      if (editingId) {
        await axiosInstance.put(`/api/PondStocking/UpdateEvent/${editingId}`, body);
        showSuccessToast('Stocking event updated');
      } else {
        await axiosInstance.post('/api/PondStocking/AddEvent', {
          PondID: parseInt(pondID, 10),
          FisherID: fisherID,
          ...body,
        });
        showSuccessToast('Stocking event added');
      }
      resetForm();
      setShowEventForm(false);
      setPreySpeciesOptions([...basePreySpeciesOptionsRef.current]);
      invalidatePond(pondID);
      if (fisherID) invalidateDashboardOwner(fisherID);
      await fetchStockingEventsList(1, false);
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleWeightUnitChange = (selectedOption) => {
    setSelectedWeightUnit(selectedOption != null ? selectedOption.value : '');
  };

  const handleLengthUnitChange = (selectedOption) => {
    setSelectedLengthUnit(selectedOption != null ? selectedOption.value : '');
  };

  const handleEdit = (ev) => {
    setShowEventForm(true);
    setEditingId(ev.id);
    setFormStockingDate(ev.StockingDate ? toDateInputValue(ev.StockingDate) || '' : '');
    setFormNotes(ev.Notes || '');
    setFormIsInitial(!!ev.IsInitial);
    setSelectedWeightUnit(
      ev.Weight_Unit != null && ev.Weight_Unit !== '' ? Number(ev.Weight_Unit) : ''
    );
    setSelectedLengthUnit(
      ev.Length_Unit != null && ev.Length_Unit !== '' ? Number(ev.Length_Unit) : ''
    );
    setFormForagePounds(
      ev.ForagePoundsAdded != null && ev.ForagePoundsAdded !== '' ? String(ev.ForagePoundsAdded) : ''
    );
    setPreservedForageTypes(forageTypesToApiString(ev.ForageTypes));
    setPreservedAdditionalForageType(
      ev.AdditionalForageType != null && String(ev.AdditionalForageType).trim()
        ? String(ev.AdditionalForageType).trim()
        : null
    );
    const pr = (ev.preyRows || []).length
      ? ev.preyRows.map((r) => ({
          FishTypeID:
            r.FishTypeID != null && r.FishTypeID !== ''
              ? r.FishTypeID
              : r.ForageTypeName != null && String(r.ForageTypeName).trim()
                ? String(r.ForageTypeName).trim()
                : null,
          Tag:
            r.Tag != null && String(r.Tag).trim()
              ? String(r.Tag).trim()
              : r.Number != null && r.Number !== ''
                ? String(r.Number)
                : '',
          TotalWeight: r.TotalWeight != null ? String(r.TotalWeight) : '',
          MinLength: r.MinLength != null ? String(r.MinLength) : '',
        }))
      : [emptyPreyRow()];
    const apiPred = ev.predatorRows || [];
    const pdBulk = [];
    const pdInd = [];
    for (const r of apiPred) {
      const isBatch = r.IsBatch === true || r.IsBatch === 1;
      if (isBatch) {
        pdBulk.push({
          FishTypeID: r.FishTypeID,
          Tag:
            r.Number != null && r.Number !== ''
              ? String(r.Number)
              : r.number != null && r.number !== ''
                ? String(r.number)
                : '',
          TotalWeight: r.TotalWeight != null ? String(r.TotalWeight) : '',
          MinLength: r.Length != null ? String(r.Length) : '',
        });
      } else {
        pdInd.push({
          FishTypeID: r.FishTypeID,
          Number:
            r.Number != null && r.Number !== ''
              ? String(r.Number)
              : r.number != null && r.number !== ''
                ? String(r.number)
                : '',
          Length: r.Length != null ? String(r.Length) : '',
          Weight: r.Weight != null ? String(r.Weight) : '',
          Tag: r.Tag || '',
        });
      }
    }
    setPreyRows(pr);
    setPredatorBulkRows(pdBulk.length ? pdBulk : [emptyPredatorBulkRow()]);
    setPredatorRows(pdInd.length ? pdInd : [emptyPredatorRow()]);
    setPreySpeciesOptions(
      mergePreyOptionsFromEventRows(basePreySpeciesOptionsRef.current, ev.preyRows)
    );
    setTimeout(() => {
      document.getElementById('stocking-event-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this stocking event?')) return;
    try {
      setSaving(true);
      await axiosInstance.delete(`/api/PondStocking/DeleteEvent/${eventId}`, {
        params: stockingFisherParams(),
      });
      showSuccessToast('Deleted');
      if (editingId === eventId) {
        resetForm();
        setShowEventForm(false);
        setPreySpeciesOptions([...basePreySpeciesOptionsRef.current]);
      }
      invalidatePond(pondID);
      if (fisherID) invalidateDashboardOwner(fisherID);
      await fetchStockingEventsList(stockingEventsPage, false);
    } catch (error) {
      showErrorToast(error.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-workflow-page py-3">
      <div className="container-fluid px-3 px-md-4">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4">
          <div>
            <ul className="breadcrumb mb-2" style={{ fontSize: '0.875rem' }}>
              <li className="breadcrumb-item">
                <Link to="/" style={{ color: '#6c757d', textDecoration: 'none' }}>Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pond" style={{ color: '#6c757d', textDecoration: 'none' }}>Pond</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#495057' }}>Stocking Data</li>
            </ul>
            <h1
              className="mb-0 page-header d-flex align-items-center gap-3"
              style={{ fontSize: '1.75rem', fontWeight: '600', color: '#2d353c' }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-3 text-white shadow-sm"
                style={{
                  width: 52,
                  height: 52,
                  background: 'linear-gradient(135deg, #348fe2 0%, #2c7bc4 100%)',
                }}
                aria-hidden
              >
                <i className="fas fa-water" style={{ fontSize: '1.35rem' }} aria-hidden />
              </span>
              Stocking Data
            </h1>
          </div>
          {canEditStocking && (
            <div className="ms-md-auto">
              <button
                type="button"
                className="btn btn-success px-3 px-md-4 rounded-pill shadow-sm"
                onClick={openAddEventForm}
              >
                <i className="fas fa-plus-circle me-2" />
                Add event
              </button>
            </div>
          )}
        </div>

        <div className="card border-0 shadow-sm mb-4 donezo-workflow-tabs">
          <ul className="nav nav-tabs border-0">
            <li className="nav-item me-2">
              <Link
                to={`/updatePond/${pondID}`}
                className="nav-link px-3 py-2 rounded-top"
              >
                <i className="fas fa-cog me-2" aria-hidden />General
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={`/pondFeedFertilization/${pondID}`}
                className="nav-link px-3 py-2 rounded-top"
              >
                <i className="fas fa-leaf me-2" aria-hidden />
                Feed &amp; Fertilization
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={`/pondStocking/${pondID}`}
                className="nav-link px-3 py-2 rounded-top donezo-tab-active"
              >
                <i className="fas fa-water me-2" aria-hidden />Stocking Data
              </Link>
            </li>
            <li className="nav-item me-2">
              <Link
                to={`/pondDetailViaPond/${pondID}`}
                className="nav-link px-3 py-2 rounded-top"
              >
                <i className="fas fa-chart-line me-2" aria-hidden />
                Fishing / Sampling Data
              </Link>
            </li>
          </ul>
        </div>

        {/* Events list — hidden while add/edit form is open */}
        {!showEventForm && (
        <div className="card border-0 shadow-sm mb-4 donezo-workflow-card">
          <div className="card-header border-0 py-3 px-3 px-md-4 donezo-workflow-card__header">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h5 className="mb-0 donezo-workflow-card__title">
                <i className="fas fa-clipboard-list me-2 text-primary" />
                Stocking events
              </h5>
              <span className="badge rounded-pill donezo-stocking-events-badge">
                {stockingEventsTotal} {stockingEventsTotal === 1 ? 'event' : 'events'}
              </span>
            </div>
          </div>
          <div className="card-body p-0">
            {stockingEventsTotal === 0 ? (
              <div className="p-4 text-center text-muted">
                <i className="fas fa-water fa-2x mb-3 d-block opacity-40 text-primary" />
                <p className="mb-2">No stocking events yet.</p>
                {canEditStocking && (
                  <button type="button" className="btn btn-success btn-sm rounded-pill mt-1" onClick={openAddEventForm}>
                    <i className="fas fa-plus-circle me-2" />
                    Add your first event
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="d-none d-md-block table-responsive">
                  <table className="table table-hover align-middle mb-0 stocking-events-table" style={{ borderSpacing: '0 14px', borderCollapse: 'separate', border: '1px solid #212529', borderRadius: '10px', overflow: 'hidden' }}>
                    <thead>
                      <tr className="donezo-stocking-summary-thead">
                        <th style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem' }}>
                          <i className="fas fa-calendar-day me-2 text-primary opacity-75" aria-hidden />
                          Date
                        </th>
                        <th className="text-center" style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem' }}>
                          <i className="fas fa-flag-checkered me-2 text-info opacity-75" aria-hidden />
                          Stocking
                        </th>
                        <th className="text-center" style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem' }}>
                          <i className="fas fa-ruler-combined me-2 text-secondary opacity-75" aria-hidden />
                          Units
                        </th>
                        <th className="text-center" style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem' }}>
                          <i className="fas fa-fish me-2 text-info opacity-75" aria-hidden />
                          Forage
                        </th>
                        <th className="text-center" style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem' }}>
                          <i className="fas fa-fish-fins me-2 text-warning opacity-75" aria-hidden />
                          Predator
                        </th>
                        <th className="text-end" style={{ padding: '12px 14px', fontWeight: '600', color: '#2d353c', fontSize: '0.9rem', width: '120px' }}>
                          <span className="visually-hidden">Actions</span>
                          <i className="fas fa-ellipsis-h text-secondary opacity-75" aria-hidden title="Actions" />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev, evIndex) => {
                        const listedForage = (ev.preyRows || []).filter(forageRowIsListed);
                        const predatorSummaries = buildEventPredatorSummaries(
                          ev,
                          pondFishSpeciesOptions,
                          fishTypesById
                        );
                        const detailsCollapsed = collapsedStockingDetailIds.has(String(ev.id));
                        return (
                          <Fragment key={ev.id}>
                            <tr
                              className={`stocking-events-main-row ${detailsCollapsed ? 'stocking-events-main-row--collapsed' : ''}`}
                              style={{
                                border: 'none',
                                borderRadius: detailsCollapsed ? '12px' : '12px 12px 0 0',
                              }}
                            >
                              <td style={{ padding: '12px 14px' }}>{ev.StockingDate || '—'}</td>
                              <td className="text-center" style={{ padding: '12px 14px' }}>
                                {ev.IsInitial ? 'Initial' : 'Supplemental'}
                              </td>
                              <td className="text-center small text-muted" style={{ padding: '12px 14px' }}>
                                {formatStockingUnitsDisplay(ev)}
                              </td>
                              <td className="text-center" style={{ padding: '12px 14px' }}>
                                {sumPreyTotalNumbers(ev.preyRows).toLocaleString()}
                              </td>
                              <td className="text-center" style={{ padding: '12px 14px' }}>
                                {sumPredatorHeadcount(ev.predatorRows).toLocaleString()}
                              </td>
                              <td className="text-end" style={{ padding: '8px 12px' }}>
                                <div className="d-inline-flex align-items-center justify-content-end gap-0">
                                  <button
                                    type="button"
                                    className="stocking-events-detail-toggle"
                                    onClick={() => toggleStockingEventDetails(ev.id)}
                                    aria-expanded={!detailsCollapsed}
                                    aria-label={
                                      detailsCollapsed
                                        ? 'Show forage and predator details'
                                        : 'Hide forage and predator details'
                                    }
                                    title={detailsCollapsed ? 'Show details' : 'Hide details'}
                                  >
                                    <i
                                      className={`fas ${detailsCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}
                                      style={{ fontSize: '0.7rem' }}
                                      aria-hidden
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-1 text-primary text-decoration-none"
                                    style={{ lineHeight: 1 }}
                                    title="Edit"
                                    aria-label="Edit event"
                                    onClick={() => handleEdit(ev)}
                                  >
                                    <i className="fas fa-pen" style={{ fontSize: '0.95rem' }} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-1 text-danger text-decoration-none"
                                    style={{ lineHeight: 1 }}
                                    title="Delete"
                                    aria-label="Delete event"
                                    disabled={!canEditStocking}
                                    onClick={() => handleDelete(ev.id)}
                                  >
                                    <i className="fas fa-trash-alt" style={{ fontSize: '0.95rem' }} aria-hidden />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {!detailsCollapsed && (
                            <tr
                              className="stocking-events-detail-row"
                              style={{
                                border: 'none',
                                borderTop: 'none',
                              }}
                            >
                              <td colSpan={6} className="stocking-events-detail-cell" style={{ background: 'inherit' }}>
                                <div className="stocking-events-detail-wrap">
                                  <h6 className="stocking-events-detail-title">
                                    <i className="fas fa-fish text-info" aria-hidden />
                                    Forage details
                                  </h6>
                                  <table className="table table-sm table-bordered stocking-events-nested-table">
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th className="text-center">Total #</th>
                                        <th className="text-center">Total wt</th>
                                        <th className="text-center">AVG len</th>
                                        <th className="text-center">AVG wt</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {listedForage.length > 0 ? (
                                        listedForage.map((row, idx) => (
                                          <tr key={row.id ?? `f-${ev.id}-${idx}`}>
                                            <td className="fw-medium text-dark">
                                              {forageTypeLabelFromApiRow(row)}
                                            </td>
                                            <td className="text-center">
                                              {formatForageTotalNumberDisplay(row)}
                                            </td>
                                            <td className="text-center">
                                              {formatStockingFloatCell(row.TotalWeight)}
                                            </td>
                                            <td className="text-center">
                                              {formatStockingFloatCell(row.MinLength)}
                                            </td>
                                            <td className="text-center">
                                              {formatForageAvgWeightFromApiRow(row) || '—'}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={5}
                                            className="text-center text-muted fst-italic stocking-events-nested-empty"
                                          >
                                            No forage entries
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>

                                  <h6 className="stocking-events-detail-title stocking-events-detail-title--follow">
                                    <i className="fas fa-fish-fins text-warning" aria-hidden />
                                    Predator summary
                                  </h6>
                                  <table className="table table-sm table-bordered stocking-events-nested-table">
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th className="text-end">Total #</th>
                                        <th className="text-end">Total wt</th>
                                        <th className="text-end">AVG len</th>
                                        <th className="text-end">AVG wt</th>
                                        <th className="text-end">AVG Wr</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {predatorSummaries.length > 0 ? (
                                        predatorSummaries.map((s, idx) => (
                                          <tr key={s.typeKey ?? `p-${ev.id}-${idx}`}>
                                            <td className="fw-medium text-dark">
                                              <SpeciesTableLabel
                                                name={s.typeFullName || s.typeLabel}
                                                abbreviation={s.typeLabel}
                                              />
                                            </td>
                                            <td className="text-end">{s.totalNumber}</td>
                                            <td className="text-end">
                                              {formatPredatorSummaryNumber(s.totalWeight)}
                                            </td>
                                            <td className="text-end text-muted">
                                              {s.avgLength == null ? '—' : formatPredatorSummaryNumber(s.avgLength)}
                                            </td>
                                            <td className="text-end text-muted">
                                              {s.avgWeight == null ? '—' : formatPredatorSummaryNumber(s.avgWeight)}
                                            </td>
                                            <td className="text-end text-muted">
                                              {s.avgWr == null ? '—' : formatWrAsPercent(s.avgWr)}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td
                                            colSpan={6}
                                            className="text-center text-muted fst-italic stocking-events-nested-empty"
                                          >
                                            No predator data
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="d-md-none p-2 p-md-3">
                  {events.map((ev) => {
                    const listedForage = (ev.preyRows || []).filter(forageRowIsListed);
                    const predatorSummaries = buildEventPredatorSummaries(
                      ev,
                      pondFishSpeciesOptions,
                      fishTypesById
                    );
                    const detailsCollapsed = collapsedStockingDetailIds.has(String(ev.id));
                    return (
                      <div key={ev.id} className="card mb-3 shadow-sm border-0" style={{ borderRadius: '10px' }}>
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div
                              className="fw-semibold text-dark text-truncate flex-grow-1 me-2"
                              style={{ minWidth: 0 }}
                            >
                              {ev.StockingDate || '—'}
                            </div>
                            <div className="d-inline-flex align-items-center justify-content-end flex-shrink-0 gap-0">
                              <button
                                type="button"
                                className="stocking-events-detail-toggle"
                                onClick={() => toggleStockingEventDetails(ev.id)}
                                aria-expanded={!detailsCollapsed}
                                aria-label={
                                  detailsCollapsed
                                    ? 'Show forage and predator details'
                                    : 'Hide forage and predator details'
                                }
                                title={detailsCollapsed ? 'Show details' : 'Hide details'}
                              >
                                <i
                                  className={`fas ${detailsCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`}
                                  style={{ fontSize: '0.75rem' }}
                                  aria-hidden
                                />
                              </button>
                              <button
                                type="button"
                                className="btn btn-link p-1 text-primary text-decoration-none"
                                style={{ lineHeight: 1 }}
                                title="Edit"
                                aria-label="Edit event"
                                onClick={() => handleEdit(ev)}
                              >
                                <i className="fas fa-pen" style={{ fontSize: '1rem' }} aria-hidden />
                              </button>
                              <button
                                type="button"
                                className="btn btn-link p-1 text-danger text-decoration-none"
                                style={{ lineHeight: 1 }}
                                title="Delete"
                                aria-label="Delete event"
                                disabled={!canEditStocking}
                                onClick={() => handleDelete(ev.id)}
                              >
                                <i className="fas fa-trash-alt" style={{ fontSize: '1rem' }} aria-hidden />
                              </button>
                            </div>
                          </div>
                          <div className="small text-muted mt-1">
                            <div className="mb-1">
                              <i className="fas fa-flag-checkered me-1 text-info" aria-hidden />
                              Stocking: {ev.IsInitial ? 'Initial' : 'Supplemental'}
                            </div>
                            <div className="mb-1">
                              <i className="fas fa-ruler-combined me-1 text-secondary" aria-hidden />
                              Units: {formatStockingUnitsDisplay(ev)}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-2 small">
                              <span>
                                <i className="fas fa-fish me-1 text-info" aria-hidden />
                                Forage: {sumPreyTotalNumbers(ev.preyRows).toLocaleString()}
                              </span>
                              <span>
                                <i className="fas fa-fish-fins me-1 text-warning" aria-hidden />
                                Predator: {sumPredatorHeadcount(ev.predatorRows).toLocaleString()}
                              </span>
                            </div>
                            {!detailsCollapsed && (
                            <div className="stocking-events-detail-wrap">
                              <h6 className="stocking-events-detail-title">
                                <i className="fas fa-fish text-info" aria-hidden />
                                Forage details
                              </h6>
                              {listedForage.length > 0 ? (
                                <div className="table-responsive mb-0">
                                  <table className="table table-sm table-bordered stocking-events-nested-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th className="text-center">Total #</th>
                                        <th className="text-center">Total wt</th>
                                        <th className="text-center">AVG len</th>
                                        <th className="text-center">AVG wt</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {listedForage.map((row, idx) => (
                                        <tr key={row.id ?? `mf-${ev.id}-${idx}`}>
                                          <td className="text-dark">{forageTypeLabelFromApiRow(row)}</td>
                                          <td className="text-center">{formatForageTotalNumberDisplay(row)}</td>
                                          <td className="text-center">{formatStockingFloatCell(row.TotalWeight)}</td>
                                          <td className="text-center">{formatStockingFloatCell(row.MinLength)}</td>
                                          <td className="text-center">
                                            {formatForageAvgWeightFromApiRow(row) || '—'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-center text-muted fst-italic mb-0 stocking-events-nested-empty">
                                  No forage entries
                                </p>
                              )}
                              <h6 className="stocking-events-detail-title stocking-events-detail-title--follow">
                                <i className="fas fa-fish-fins text-warning" aria-hidden />
                                Predator summary
                              </h6>
                              {predatorSummaries.length > 0 ? (
                                <div className="table-responsive mb-0">
                                  <table className="table table-sm table-bordered stocking-events-nested-table mb-0">
                                    <thead>
                                      <tr>
                                        <th>Type</th>
                                        <th className="text-end">Total #</th>
                                        <th className="text-end">Total wt</th>
                                        <th className="text-end">AVG len</th>
                                        <th className="text-end">AVG wt</th>
                                        <th className="text-end">AVG Wr</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {predatorSummaries.map((s, idx) => (
                                        <tr key={s.typeKey ?? `mp-${ev.id}-${idx}`}>
                                          <td className="text-dark">
                                            <SpeciesTableLabel
                                              name={s.typeFullName || s.typeLabel}
                                              abbreviation={s.typeLabel}
                                            />
                                          </td>
                                          <td className="text-end">{s.totalNumber}</td>
                                          <td className="text-end">{formatPredatorSummaryNumber(s.totalWeight)}</td>
                                          <td className="text-end text-muted">
                                            {s.avgLength == null ? '—' : formatPredatorSummaryNumber(s.avgLength)}
                                          </td>
                                          <td className="text-end text-muted">
                                            {s.avgWeight == null ? '—' : formatPredatorSummaryNumber(s.avgWeight)}
                                          </td>
                                          <td className="text-end text-muted">
                                            {s.avgWr == null ? '—' : formatWrAsPercent(s.avgWr)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-center text-muted fst-italic mb-0 stocking-events-nested-empty">
                                  No predator data
                                </p>
                              )}
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 px-3 py-2 px-md-4 donezo-stocking-pagination-bar">
                  <div className="small text-muted text-center text-md-start mb-0">
                    Showing{' '}
                    <span className="donezo-stocking-pagination-bar__strong">{stockingEventsRangeStart}</span>
                    {' – '}
                    <span className="donezo-stocking-pagination-bar__strong">{stockingEventsRangeEnd}</span>
                    {' of '}
                    <span className="donezo-stocking-pagination-bar__strong">{stockingEventsTotal}</span>
                  </div>
                  <nav aria-label="Stocking events pagination">
                    <ul className="pagination pagination-sm mb-0 justify-content-center donezo-stocking-pagination">
                      <li
                        className={`page-item ${
                          stockingEventsPage <= 1 || stockingEventsListLoading ? 'disabled' : ''
                        }`}
                      >
                        <button
                          type="button"
                          className="page-link rounded-start"
                          disabled={stockingEventsPage <= 1 || stockingEventsListLoading}
                          onClick={() => handleStockingEventsPageChange(stockingEventsPage - 1)}
                        >
                          Previous
                        </button>
                      </li>
                      <li className="page-item disabled">
                        <span className="page-link donezo-stocking-pagination__current">
                          {stockingEventsPage} / {stockingEventsTotalPages}
                        </span>
                      </li>
                      <li
                        className={`page-item ${
                          stockingEventsPage >= stockingEventsTotalPages || stockingEventsListLoading
                            ? 'disabled'
                            : ''
                        }`}
                      >
                        <button
                          type="button"
                          className="page-link rounded-end"
                          disabled={
                            stockingEventsPage >= stockingEventsTotalPages || stockingEventsListLoading
                          }
                          onClick={() => handleStockingEventsPageChange(stockingEventsPage + 1)}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </>
            )}
          </div>
        </div>
        )}

        {showEventForm && (
        <form id="stocking-event-form" onSubmit={handleSaveEvent}>
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '14px', overflow: 'hidden' }}>
            <div
              className="card-header border-bottom py-3 px-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 55%)',
                borderColor: '#e2e8f0',
              }}
            >
              <h5 className="mb-0 d-flex align-items-center gap-3 flex-wrap" style={{ color: '#1e293b', fontWeight: 600 }}>
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-3 text-white flex-shrink-0 shadow-sm"
                  style={{
                    width: 44,
                    height: 44,
                    background: editingId
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                      : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  }}
                  aria-hidden
                >
                  <i className={`fas ${editingId ? 'fa-pen-to-square' : 'fa-calendar-plus'}`} style={{ fontSize: '1.1rem' }} />
                </span>
                {editingId ? 'Edit stocking event' : 'Add stocking event'}
              </h5>
              <div className="d-flex flex-wrap align-items-center gap-2 justify-content-end ms-auto">
                {canEditStocking ? (
                  <>
                    <button type="submit" className="btn btn-success px-4 rounded-pill shadow-sm" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                          Saving…
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check-circle me-2" aria-hidden />
                          {editingId ? 'Update event' : 'Save event'}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary rounded-pill px-4"
                      onClick={closeEventForm}
                    >
                      <i className="fas fa-times me-2" aria-hidden />
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-light border rounded-pill px-3 py-2 shadow-sm d-inline-flex align-items-center gap-2"
                    style={{ fontWeight: 500, color: '#475569' }}
                    onClick={closeEventForm}
                  >
                    <i className="fas fa-times-circle text-secondary" aria-hidden />
                    Close
                  </button>
                )}
              </div>
            </div>
            <div className="card-body px-3 px-md-4 py-4">
              <div className="row g-3 g-md-4 mb-4">
                <div className="col-12 col-lg-4">
                  <div
                    className="h-100 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <label className="form-label small fw-semibold text-secondary d-flex align-items-center gap-2 mb-2">
                      <i className="fas fa-calendar-day text-primary" aria-hidden />
                      Stocking date
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0 text-muted">
                        <i className="fas fa-calendar-alt" aria-hidden />
                      </span>
                      <input
                        type="date"
                        className="form-control border-start-0 ps-0"
                        value={formStockingDate}
                        onChange={(e) => setFormStockingDate(e.target.value)}
                        disabled={!canEditStocking}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-8">
                  <div
                    className="h-100 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <label className="form-label small fw-semibold text-secondary d-flex align-items-center gap-2 mb-2">
                      <i className="fas fa-note-sticky text-primary" aria-hidden />
                      Notes
                    </label>
                    <div className="input-group align-items-start">
                      <span className="input-group-text bg-white border-end-0 text-muted">
                        <i className="fas fa-align-left" aria-hidden />
                      </span>
                      <textarea
                        className="form-control border-start-0 ps-0"
                        placeholder="Optional notes for this event"
                        value={formNotes}
                        onChange={(e) => setFormNotes(e.target.value)}
                        disabled={!canEditStocking}
                        rows={3}
                        style={{ resize: "vertical", minHeight: "4.5rem" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-12 col-lg-4">
                  <div
                    className="h-100 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <div className="form-check mt-1">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="stockingFormIsInitial"
                        checked={formIsInitial}
                        onChange={(e) => setFormIsInitial(e.target.checked)}
                        disabled={!canEditStocking}
                      />
                      <label className="form-check-label small fw-semibold text-secondary" htmlFor="stockingFormIsInitial">
                        Initial stocking
                      </label>
                    </div>
                    <p className="small text-muted mb-0 mt-2">
                      Mark if this event is the pond&apos;s initial stocking.
                    </p>
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="h-100 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <label className="form-label small fw-semibold text-secondary d-flex align-items-center gap-2 mb-2">
                      <i className="fas fa-weight-hanging text-primary" aria-hidden />
                      Weight unit
                    </label>
                    <WeightUnitDropDownSelection
                      selectedValue={selectedWeightUnit}
                      onChange={handleWeightUnitChange}
                      isDisabled={!canEditStocking}
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 col-lg-4">
                  <div
                    className="h-100 p-3 rounded-3"
                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
                  >
                    <label className="form-label small fw-semibold text-secondary d-flex align-items-center gap-2 mb-2">
                      <i className="fas fa-ruler-horizontal text-primary" aria-hidden />
                      Length unit
                    </label>
                    <LengthUnitDropDownSelection
                      selectedValue={selectedLengthUnit}
                      onChange={handleLengthUnitChange}
                      isDisabled={!canEditStocking}
                    />
                  </div>
                </div>
                {/* Hidden: pounds of forage added (form field removed from UI).
                <div className="col-12 col-md-4">
                  <label className="form-label small text-muted">Pounds of forage added</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={formForagePounds}
                    onChange={(e) => setFormForagePounds(e.target.value)}
                    disabled={!canEditStocking}
                  />
                </div>
                */}
              </div>

              <div
                className="card border-0 shadow-sm mb-3"
                style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
              >
                <div
                  className="card-header bg-white border-bottom py-3 px-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{ borderRadius: '12px 12px 0 0' }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: '#2d353c', fontSize: '1rem' }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{ width: 36, height: 36, background: 'rgba(13, 202, 240, 0.12)' }}
                      aria-hidden
                    >
                      <i className="fas fa-fish text-info" style={{ fontSize: '1rem' }} />
                    </span>
                    Forage
                  </h6>
                </div>
                <div className="card-body p-2 p-md-3 pt-md-2">
                  <div className="table-responsive mb-0 stocking-table-shell">
                    <table className="table table-sm mb-0 stocking-stock-table align-middle">
                      <colgroup>
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '21%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <i className="fas fa-tag me-1 text-primary opacity-75" aria-hidden />
                            Type
                          </th>
                          <th>
                            <i className="fas fa-hashtag me-1 text-primary opacity-75" aria-hidden />
                            Total #
                          </th>
                          <th>
                            <i className="fas fa-weight-hanging me-1 text-primary opacity-75" aria-hidden />
                            Total wt
                          </th>
                          <th>
                            <i className="fas fa-ruler-horizontal me-1 text-primary opacity-75" aria-hidden />
                            AVG len
                          </th>
                          <th>
                            <i className="fas fa-balance-scale me-1 text-primary opacity-75" aria-hidden />
                            AVG wt
                          </th>
                          <th className="text-center" style={{ width: '10%' }}>
                            <i className="fas fa-bars me-1 text-secondary" aria-hidden title="Row actions" />
                            <span className="visually-hidden">Add / remove</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {preyRows.map((row, idx) => (
                          <tr key={`p-${idx}`}>
                            <td style={{ minWidth: 0 }}>
                              <FishSpeciesDropDownSelection
                                allowMultiple={false}
                                selectedValue={row.FishTypeID}
                                options={preySpeciesOptions}
                                fullWidth
                                placeholder="Select forage type"
                                noOptionsMessage={() =>
                                  'Choose forage types and additional forage on the pond General tab first.'
                                }
                                onChange={(v) => {
                                  const n = [...preyRows];
                                  n[idx] = { ...n[idx], FishTypeID: v };
                                  setPreyRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                className="form-control form-control-sm w-100"
                                value={row.Tag}
                                onChange={(e) => {
                                  const n = [...preyRows];
                                  n[idx] = { ...n[idx], Tag: e.target.value };
                                  setPreyRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm w-100"
                                value={row.TotalWeight}
                                onChange={(e) => {
                                  const n = [...preyRows];
                                  n[idx] = { ...n[idx], TotalWeight: e.target.value };
                                  setPreyRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm w-100"
                                value={row.MinLength}
                                onChange={(e) => {
                                  const n = [...preyRows];
                                  n[idx] = { ...n[idx], MinLength: e.target.value };
                                  setPreyRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                readOnly
                                className="form-control form-control-sm w-100 bg-light"
                                value={formatPreyAvgWeightDisplay(row)}
                                title="Total weight ÷ total number"
                              />
                            </td>
                            <td className="text-center align-middle" style={{ minWidth: '76px' }}>
                              <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                {idx === preyRows.length - 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                    style={{ width: '36px', height: '36px' }}
                                    disabled={!canEditStocking}
                                    title="Add forage row"
                                    aria-label="Add forage row"
                                    onClick={() => setPreyRows((r) => [...r, emptyPreyRow()])}
                                  >
                                    <i
                                      className="fas fa-plus-circle"
                                      style={{ fontSize: '1.15rem', lineHeight: 1 }}
                                      aria-hidden
                                    />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                  style={{ width: '36px', height: '36px' }}
                                  disabled={!canEditStocking || preyRows.length <= 1}
                                  title="Remove row"
                                  aria-label="Remove forage row"
                                  onClick={() => setPreyRows((r) => r.filter((_, i) => i !== idx))}
                                >
                                  <i
                                    className="fas fa-times-circle"
                                    style={{ fontSize: '1.1rem', lineHeight: 1 }}
                                    aria-hidden
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div
                className="card border-0 shadow-sm mb-0"
                style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}
              >
                <div
                  className="card-header bg-white border-bottom py-3 px-3 px-md-4 d-flex flex-wrap align-items-center justify-content-between gap-2"
                  style={{ borderRadius: '12px 12px 0 0' }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: '#2d353c', fontSize: '1rem' }}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
                      style={{ width: 36, height: 36, background: 'rgba(245, 158, 11, 0.15)' }}
                      aria-hidden
                    >
                      <i className="fas fa-fish-fins text-warning" style={{ fontSize: '1rem' }} />
                    </span>
                    Predator
                  </h6>
                  {canEditStocking && (
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                        onClick={exportPredatorRowsToExcel}
                        title="Export the predator rows currently in this form"
                      >
                        <i className="fas fa-file-export me-1" aria-hidden />
                        Download xlsx
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm rounded-pill px-3 text-white"
                        style={{ backgroundColor: '#41848F', borderColor: '#41848F' }}
                        onClick={() => {
                          setPredatorImportModalOpen(true);
                          setPredatorImportReportComplete(false);
                          setPredatorImportProcessedRows([]);
                        }}
                      >
                        <i className="fas fa-file-import me-1" aria-hidden />
                        Import Excel
                      </button>
                    </div>
                  )}
                </div>
                <div className="card-body p-2 p-md-3 pt-md-2">
                  <h6
                    className="small fw-bold text-uppercase text-secondary mb-2"
                    style={{ letterSpacing: '0.04em', fontSize: '0.72rem' }}
                  >
                    Predator (overall)
                  </h6>
                  {predatorOverallSummaries.length > 0 ? (
                    <div className="table-responsive mb-3 stocking-table-shell">
                      <table className="table table-sm table-bordered mb-0 align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>
                              <i className="fas fa-fish me-1 text-warning opacity-75" aria-hidden />
                              Type
                            </th>
                            <th className="text-end">
                              <i className="fas fa-hashtag me-1 text-warning opacity-75" aria-hidden />
                              Total #
                            </th>
                            <th className="text-end">
                              <i className="fas fa-weight-hanging me-1 text-warning opacity-75" aria-hidden />
                              Total wt
                            </th>
                            <th className="text-end">
                              <i className="fas fa-ruler-vertical me-1 text-warning opacity-75" aria-hidden />
                              AVG len
                            </th>
                            <th className="text-end">
                              <i className="fas fa-balance-scale me-1 text-warning opacity-75" aria-hidden />
                              AVG wt
                            </th>
                            <th className="text-end">
                              <i className="fas fa-percent me-1 text-warning opacity-75" aria-hidden />
                              AVG Wr
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {predatorOverallSummaries.map((s) => (
                            <tr key={s.typeKey}>
                              <td className="fw-medium">
                                <SpeciesTableLabel
                                  name={s.typeFullName || s.typeLabel}
                                  abbreviation={s.typeLabel}
                                />
                              </td>
                              <td className="text-end">{s.totalNumber}</td>
                              <td className="text-end">{formatPredatorSummaryNumber(s.totalWeight)}</td>
                              <td className="text-end text-muted">
                                {s.avgLength == null ? '—' : formatPredatorSummaryNumber(s.avgLength)}
                              </td>
                              <td className="text-end text-muted">
                                {s.avgWeight == null ? '—' : formatPredatorSummaryNumber(s.avgWeight)}
                              </td>
                              <td className="text-end text-muted">
                                {s.avgWr == null ? '—' : formatWrAsPercent(s.avgWr)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="small text-muted mb-3">
                      Summary by fish type appears when you add batch or individual predator rows with a type
                      (and count) below.
                    </p>
                  )}

                  <h6
                    className="small fw-bold text-uppercase text-secondary mb-2 mt-1"
                    style={{ letterSpacing: '0.04em', fontSize: '0.72rem' }}
                  >
                    Predator (batch)
                  </h6>
                  <p className="small text-muted mb-2">
                    Small fingerling predators: enter totals like forage (no tag or per-fish length/weight).
                  </p>
                  <div className="table-responsive mb-3 stocking-table-shell">
                    <table className="table table-sm mb-0 stocking-stock-table align-middle">
                      <colgroup>
                        <col style={{ width: '18%' }} />
                        <col style={{ width: '12%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '21%' }} />
                        <col style={{ width: '10%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>
                            <i className="fas fa-tag me-1 text-warning opacity-75" aria-hidden />
                            Type
                          </th>
                          <th>
                            <i className="fas fa-hashtag me-1 text-warning opacity-75" aria-hidden />
                            Total #
                          </th>
                          <th>
                            <i className="fas fa-weight-hanging me-1 text-warning opacity-75" aria-hidden />
                            Total wt
                          </th>
                          <th>
                            <i className="fas fa-ruler-horizontal me-1 text-warning opacity-75" aria-hidden />
                            AVG len
                          </th>
                          <th>
                            <i className="fas fa-balance-scale me-1 text-warning opacity-75" aria-hidden />
                            AVG wt
                          </th>
                          <th className="text-center" style={{ width: '10%' }}>
                            <i className="fas fa-bars me-1 text-secondary" aria-hidden title="Row actions" />
                            <span className="visually-hidden">Add / remove</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {predatorBulkRows.map((row, idx) => (
                          <tr key={`pb-${idx}`}>
                            <td style={{ minWidth: 0 }}>
                              <FishSpeciesDropDownSelection
                                allowMultiple={false}
                                selectedValue={row.FishTypeID}
                                options={pondFishSpeciesOptions}
                                fullWidth
                                placeholder="Select predator species"
                                onChange={(v) => {
                                  const n = [...predatorBulkRows];
                                  n[idx] = { ...n[idx], FishTypeID: v };
                                  setPredatorBulkRows(n);
                                }}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                className="form-control form-control-sm w-100"
                                value={row.Tag}
                                onChange={(e) => {
                                  const n = [...predatorBulkRows];
                                  n[idx] = { ...n[idx], Tag: e.target.value };
                                  setPredatorBulkRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm w-100"
                                value={row.TotalWeight}
                                onChange={(e) => {
                                  const n = [...predatorBulkRows];
                                  n[idx] = { ...n[idx], TotalWeight: e.target.value };
                                  setPredatorBulkRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control form-control-sm w-100"
                                value={row.MinLength}
                                onChange={(e) => {
                                  const n = [...predatorBulkRows];
                                  n[idx] = { ...n[idx], MinLength: e.target.value };
                                  setPredatorBulkRows(n);
                                }}
                                disabled={!canEditStocking}
                              />
                            </td>
                            <td style={{ minWidth: 0 }}>
                              <input
                                type="text"
                                readOnly
                                className="form-control form-control-sm w-100 bg-light"
                                value={formatPreyAvgWeightDisplay(row)}
                                title="Total weight ÷ total number"
                              />
                            </td>
                            <td className="text-center align-middle" style={{ minWidth: '76px' }}>
                              <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                {idx === predatorBulkRows.length - 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-success btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                    style={{ width: '36px', height: '36px' }}
                                    disabled={!canEditStocking}
                                    title="Add batch predator row"
                                    aria-label="Add batch predator row"
                                    onClick={() =>
                                      setPredatorBulkRows((r) => [...r, emptyPredatorBulkRow()])
                                    }
                                  >
                                    <i
                                      className="fas fa-plus-circle"
                                      style={{ fontSize: '1.15rem', lineHeight: 1 }}
                                      aria-hidden
                                    />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                  style={{ width: '36px', height: '36px' }}
                                  disabled={!canEditStocking || predatorBulkRows.length <= 1}
                                  title="Remove row"
                                  aria-label="Remove batch predator row"
                                  onClick={() =>
                                    setPredatorBulkRows((r) => r.filter((_, i) => i !== idx))
                                  }
                                >
                                  <i
                                    className="fas fa-times-circle"
                                    style={{ fontSize: '1.1rem', lineHeight: 1 }}
                                    aria-hidden
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <h6
                    className="small fw-bold text-uppercase text-secondary mb-2 mt-1"
                    style={{ letterSpacing: '0.04em', fontSize: '0.72rem' }}
                  >
                    Predator (individual)
                  </h6>
                  <div className="table-responsive mb-0 stocking-table-shell">
                    <table className="table table-sm mb-0 stocking-stock-table align-middle">
                  <colgroup>
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '10%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>
                        <i className="fas fa-fish me-1 text-warning opacity-75" aria-hidden />
                        Type
                      </th>
                      <th>
                        <i className="fas fa-ruler-vertical me-1 text-warning opacity-75" aria-hidden />
                        Length
                      </th>
                      <th>
                        <i className="fas fa-weight-hanging me-1 text-warning opacity-75" aria-hidden />
                        Weight
                      </th>
                      <th>
                        <i className="fas fa-chart-line me-1 text-warning opacity-75" aria-hidden />
                        Ws
                      </th>
                      <th>
                        <i className="fas fa-percent me-1 text-warning opacity-75" aria-hidden />
                        Wr
                      </th>
                      <th>
                        <i className="fas fa-barcode me-1 text-warning opacity-75" aria-hidden />
                        Tag #
                      </th>
                      <th className="text-center" style={{ width: '10%' }}>
                        <i className="fas fa-bars me-1 text-secondary" aria-hidden title="Row actions" />
                        <span className="visually-hidden">Add / remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {predatorRows.map((row, idx) => {
                      const { ws, wr } = getPredatorWsWrForRow(row);
                      return (
                      <tr key={`d-${idx}`}>
                        <td style={{ minWidth: 0 }}>
                          <FishSpeciesDropDownSelection
                            allowMultiple={false}
                            selectedValue={row.FishTypeID}
                            options={pondFishSpeciesOptions}
                            fullWidth
                            onChange={(v) => {
                              const n = [...predatorRows];
                              n[idx] = { ...n[idx], FishTypeID: v };
                              setPredatorRows(n);
                            }}
                          />
                        </td>
                        <td style={{ minWidth: 0 }}>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm w-100"
                            value={row.Length}
                            onChange={(e) => {
                              const n = [...predatorRows];
                              n[idx] = { ...n[idx], Length: e.target.value };
                              setPredatorRows(n);
                            }}
                            disabled={!canEditStocking}
                          />
                        </td>
                        <td style={{ minWidth: 0 }}>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm w-100"
                            value={row.Weight}
                            onChange={(e) => {
                              const n = [...predatorRows];
                              n[idx] = { ...n[idx], Weight: e.target.value };
                              setPredatorRows(n);
                            }}
                            disabled={!canEditStocking}
                          />
                        </td>
                        <td style={{ minWidth: 0 }}>
                          <input
                            type="text"
                            readOnly
                            className="form-control form-control-sm w-100 bg-light"
                            value={ws === '' ? '' : String(ws)}
                            title="Standard weight from fish-type equation (length unit mm → WsMG, else WsIP)"
                          />
                        </td>
                        <td style={{ minWidth: 0 }}>
                          <input
                            type="text"
                            readOnly
                            className="form-control form-control-sm w-100 bg-light"
                            value={formatWrAsPercent(wr)}
                            title="Relative weight (actual ÷ standard), shown as percent"
                          />
                        </td>
                        <td style={{ minWidth: 0 }}>
                          <input
                            type="text"
                            inputMode="text"
                            className="form-control form-control-sm w-100"
                            value={row.Tag}
                            onChange={(e) => {
                              const n = [...predatorRows];
                              n[idx] = { ...n[idx], Tag: e.target.value };
                              setPredatorRows(n);
                            }}
                            disabled={!canEditStocking}
                          />
                        </td>
                        <td className="text-center align-middle" style={{ minWidth: '76px' }}>
                          <div className="d-inline-flex align-items-center justify-content-center gap-1">
                            {idx === predatorRows.length - 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-success btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                                style={{ width: '36px', height: '36px' }}
                                disabled={!canEditStocking}
                                title="Add predator row"
                                aria-label="Add predator row"
                                onClick={() => setPredatorRows((r) => [...r, emptyPredatorRow()])}
                              >
                                <i className="fas fa-plus-circle" style={{ fontSize: '1.15rem', lineHeight: 1 }} aria-hidden />
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm rounded-circle d-inline-flex align-items-center justify-content-center p-0 border-2"
                              style={{ width: '36px', height: '36px' }}
                              disabled={!canEditStocking || predatorRows.length <= 1}
                              title="Remove row"
                              aria-label="Remove predator row"
                              onClick={() => setPredatorRows((r) => r.filter((_, i) => i !== idx))}
                            >
                              <i className="fas fa-times-circle" style={{ fontSize: '1.1rem', lineHeight: 1 }} aria-hidden />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-footer bg-white border-top py-3 px-3 px-md-4 d-flex flex-wrap align-items-center gap-2 justify-content-end">
              {canEditStocking ? (
                <>
                  <button type="submit" className="btn btn-success px-4 rounded-pill shadow-sm" disabled={saving}>
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check-circle me-2" aria-hidden />
                        {editingId ? 'Update event' : 'Save event'}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    onClick={closeEventForm}
                  >
                    <i className="fas fa-times me-2" aria-hidden />
                    Cancel
                  </button>
                </>
              ) : (
                <p className="text-muted small mb-0 me-auto">Only administrators can edit stocking data.</p>
              )}
            </div>
          </div>
        </form>
        )}

        {predatorImportModalOpen && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ zIndex: 1060, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="predatorImportModalTitle"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow" style={{ borderRadius: '16px', maxHeight: '92vh' }}>
                <div className="modal-header border-0 pb-2 align-items-center">
                  <h5 id="predatorImportModalTitle" className="modal-title d-flex align-items-center gap-3 flex-wrap mb-0">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                      style={{
                        width: 48,
                        height: 48,
                        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                      }}
                      aria-hidden
                    >
                      <i className="fas fa-file-excel text-warning" style={{ fontSize: '1.35rem' }} />
                    </span>
                    <span>Import stocking predators from Excel</span>
                  </h5>
                  <button
                    type="button"
                    className="btn btn-light border rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm"
                    style={{ width: 42, height: 42 }}
                    aria-label="Close"
                    disabled={isPredatorImporting}
                    onClick={closePredatorImportModal}
                  >
                    <i className="fas fa-times text-secondary" style={{ fontSize: '1.1rem' }} aria-hidden />
                  </button>
                </div>
                <div className="modal-body position-relative pt-2">
                  {isPredatorImporting && (
                    <div
                      className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
                      style={{
                        zIndex: 10,
                        backgroundColor: 'rgba(255,255,255,0.93)',
                        backdropFilter: 'blur(2px)',
                      }}
                    >
                      <div className="text-center px-3" style={{ maxWidth: 440 }}>
                        <i className="fas fa-file-import text-primary mb-3" style={{ fontSize: '2.5rem' }} aria-hidden />
                        <h4 className="h5 text-primary mb-3">Importing rows…</h4>
                        <div className="progress mb-3" style={{ height: 10 }}>
                          <div
                            className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                            style={{
                              width: `${
                                predatorImportProgress.total > 0
                                  ? Math.round(
                                      (predatorImportProgress.current / predatorImportProgress.total) * 100
                                    )
                                  : 0
                              }%`,
                            }}
                          />
                        </div>
                        <div className="mb-2">
                          <span className="badge bg-primary fs-6 px-3 py-2">
                            {predatorImportProgress.current} of {predatorImportProgress.total} rows
                          </span>
                        </div>
                        {predatorImportProgress.currentRow && (
                          <div className="card shadow-sm text-start">
                            <div className="card-header py-2 bg-light">
                              <small className="text-muted">
                                Processing Excel row {predatorImportProgress.currentRow.excelRow}
                              </small>
                            </div>
                            <div className="card-body py-2">
                              <strong>{predatorImportProgress.currentRow.label}</strong>
                              <span
                                className="spinner-border spinner-border-sm text-primary float-end"
                                role="status"
                              >
                                <span className="visually-hidden">Loading</span>
                              </span>
                            </div>
                          </div>
                        )}
                        <div className="row text-center small mt-3 g-2">
                          <div className="col-4">
                            <div className="text-success fw-bold">
                              {predatorImportProcessedRows.filter((r) => r.status === 'success').length}
                            </div>
                            <div className="text-muted">OK</div>
                          </div>
                          <div className="col-4">
                            <div className="text-danger fw-bold">
                              {predatorImportProcessedRows.filter((r) => r.status === 'error').length}
                            </div>
                            <div className="text-muted">Errors</div>
                          </div>
                          <div className="col-4">
                            <div className="text-warning fw-bold">
                              {predatorImportProcessedRows.filter((r) => r.status === 'skipped').length}
                            </div>
                            <div className="text-muted">Skipped</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {predatorImportReportComplete &&
                    !isPredatorImporting &&
                    predatorImportProcessedRows.length > 0 && (
                    <div
                      className={`alert mb-3 ${
                        predatorImportProcessedRows.some((r) => r.status === 'error')
                          ? 'alert-danger'
                          : 'alert-success'
                      }`}
                    >
                      <strong className="d-block mb-1">Import report</strong>
                      <div className="small">
                        OK: {predatorImportProcessedRows.filter((r) => r.status === 'success').length} · Skipped:{' '}
                        {predatorImportProcessedRows.filter((r) => r.status === 'skipped').length} · Errors:{' '}
                        {predatorImportProcessedRows.filter((r) => r.status === 'error').length}
                      </div>
                      {predatorImportProcessedRows.some((r) => r.status === 'error') && (
                        <ul className="small mb-0 mt-2 ps-3">
                          {predatorImportProcessedRows
                            .filter((r) => r.status === 'error')
                            .map((r) => (
                              <li key={r.excelRow}>
                                Row {r.excelRow} ({r.label}): {r.detail}
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-header bg-primary text-white py-2">
                          <i className="fas fa-download me-2" aria-hidden />
                          Step 1: Get template
                        </div>
                        <div className="card-body">
                          <p className="card-text small">
                            Download the workbook with <strong>Predator Data</strong> and a{' '}
                            <strong>Pond Fish Species List</strong> sheet (species assigned to this pond).
                          </p>
                          <button
                            type="button"
                            className="btn btn-outline-primary w-100"
                            onClick={generatePredatorExcelTemplate}
                            disabled={isPredatorImporting}
                          >
                            <i className="fas fa-file-download me-2" aria-hidden />
                            Download template
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-header text-white py-2" style={{ backgroundColor: '#41848F' }}>
                          <i className="fas fa-upload me-2" aria-hidden />
                          Step 2: Upload data
                        </div>
                        <div className="card-body">
                          <p className="card-text small">
                            Select your filled file. Preview appears below; then click Import data.
                          </p>
                          <div
                            className="upload-zone p-3 bg-light rounded text-center"
                            style={{ border: '2px dashed #ccc' }}
                            onDrop={handlePredatorImportDrop}
                            onDragOver={handlePredatorImportDragOver}
                          >
                            <input
                              ref={stockingPredatorExcelInputRef}
                              type="file"
                              id="stockingPredatorExcelFile"
                              className="d-none"
                              accept=".xlsx,.xls"
                              onChange={handlePredatorImportFileChange}
                              disabled={isPredatorImporting}
                            />
                            <label htmlFor="stockingPredatorExcelFile" className="mb-0 w-100" style={{ cursor: 'pointer' }}>
                              <span className="text-muted">
                                <i className="fas fa-cloud-upload-alt fa-2x mb-2 d-block" aria-hidden />
                                <span className="d-block">Click to select or drag and drop</span>
                                <small>(.xlsx or .xls)</small>
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {predatorImportFile && (
                    <div className="card border-0 shadow-sm mb-0">
                      <div className="card-header bg-success text-white py-2 d-flex align-items-center gap-2">
                        <i className="fas fa-file-excel" aria-hidden />
                        <span className="text-truncate">{predatorImportFile.name}</span>
                      </div>
                      {predatorImportPreview.length > 0 ? (
                        <div className="card-body p-0 table-responsive" style={{ maxHeight: 220 }}>
                          <table className="table table-sm table-hover mb-0 small">
                            <thead className="table-light sticky-top">
                              <tr>
                                {Object.keys(predatorImportPreview[0])
                                  .slice(0, 8)
                                  .map((k) => (
                                    <th key={k}>{k}</th>
                                  ))}
                              </tr>
                            </thead>
                            <tbody>
                              {predatorImportPreview.map((row, ri) => (
                                <tr key={ri}>
                                  {Object.keys(predatorImportPreview[0])
                                    .slice(0, 8)
                                    .map((k) => (
                                      <td key={k}>{row[k] != null ? String(row[k]) : ''}</td>
                                    ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="card-body small text-muted">
                          No preview available — you can still run Import data if the file uses the template.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-4"
                    disabled={isPredatorImporting}
                    onClick={closePredatorImportModal}
                  >
                    <i className="fas fa-times me-2" aria-hidden />
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn px-4 rounded-pill text-white shadow-sm"
                    style={{ backgroundColor: '#41848F', borderColor: '#41848F' }}
                    disabled={isPredatorImporting || !predatorImportFile}
                    onClick={handlePredatorImportConfirm}
                  >
                    {isPredatorImporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden />
                        Importing…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-file-import me-2" aria-hidden />
                        Import data
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockingData;
